import {
  Alert,
  Button,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';

import { FormTooltip } from '@design-system/components/tooltip/tooltip';

import { useAutomationStatsViewModel } from '../../view-model/AutomationInsights.viewModel';
import * as styles from '../Automations.view.css';

const STEP_COLUMN_HINTS: Record<string, string> = {
  'Unique clicks':
    'Campaigns where the recipient clicked a tracked link at least once. The most reliable engagement signal.',
  'Unique opens':
    'Tracking-pixel opens. Privacy proxies (e.g. Apple Mail) inflate them and image blockers hide them, so treat them as a rough signal and judge by clicks.',
};

const HeaderCellWithHint = ({ label }: { label: string }) => (
  <TableCell align="right">
    <span className={styles.labelWithHint}>
      {label}
      <FormTooltip title={STEP_COLUMN_HINTS[label]} />
    </span>
  </TableCell>
);

export const AutomationStatsView = ({ creatorId }: { creatorId: number }) => {
  const vm = useAutomationStatsViewModel(creatorId);

  if (vm.type === 'not-fetched' || vm.type === 'fetching') {
    return <Skeleton variant="rounded" height={220} />;
  }
  if (vm.type === 'fetch-error') {
    return (
      <Alert
        severity="error"
        action={
          <Button onClick={() => void vm.value.retry()}>Try again</Button>
        }
      >
        {vm.value.message}
      </Alert>
    );
  }

  return (
    <>
      {vm.value.cards.map((card) => (
        <div className={styles.card} key={card.automationType}>
          <div>
            <Typography variant="h6">{card.title}</Typography>
            <Typography className={styles.muted} variant="body2">
              {card.triggerLine}
            </Typography>
          </div>
          <div className={styles.headline}>
            {card.headline.map((item) => (
              <div className={styles.headlineItem} key={item.label}>
                <Typography variant="h5">{item.value}</Typography>
                <span className={styles.labelWithHint}>
                  <Typography className={styles.muted} variant="body2">
                    {item.label}
                  </Typography>
                  <FormTooltip title={item.hint} />
                </span>
              </div>
            ))}
          </div>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Step</TableCell>
                <TableCell align="right">Sent</TableCell>
                <HeaderCellWithHint label="Unique clicks" />
                <HeaderCellWithHint label="Unique opens" />
              </TableRow>
            </TableHead>
            <TableBody>
              {card.steps.map((step) => (
                <TableRow key={step.key}>
                  <TableCell>{step.label}</TableCell>
                  <TableCell align="right">{step.sent}</TableCell>
                  <TableCell align="right">{step.uniqueClicks}</TableCell>
                  <TableCell align="right" className={styles.muted}>
                    {step.uniqueOpens}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {card.drainingLine ? (
            <Typography className={styles.muted} variant="body2">
              {card.drainingLine}
            </Typography>
          ) : null}
          <div className={styles.closedLine}>
            <Typography variant="body2">
              Closed enrollments: {card.discrepancy.total}
            </Typography>
            <FormTooltip title="Why enrollments ended. Goal reached means the person converted; the other reasons closed without a purchase." />
            {card.discrepancy.reasons.map((reason) => (
              <Typography
                className={styles.muted}
                variant="body2"
                key={reason.key}
              >
                {reason.label}: {reason.count}
              </Typography>
            ))}
          </div>
        </div>
      ))}
    </>
  );
};
